/**
 * ArrivePatientDialog — Set arrive-patient status on a study.
 *
 * Offers "Waiting for Documentation" (W) and "Ready for Exam" (R) options,
 * plus a clear option. Calls api.arrivePatient(objKey, status).
 */
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Alert,
} from '@mui/material';
import { api } from '../../api/rpc';
import type { Study } from '../../api/types';

interface ArrivePatientDialogProps {
  open: boolean;
  studies: Study[];
  onClose: () => void;
  onComplete?: () => void;
}

export const ArrivePatientDialog = ({
  open,
  studies,
  onClose,
  onComplete,
}: ArrivePatientDialogProps) => {
  const [status, setStatus] = useState<'W' | 'R' | ''>('W');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const handleSubmit = async () => {
    setPending(true);
    setError(null);
    try {
      const val = status === '' ? null : status;
      for (const s of studies) {
        await api.arrivePatient(s.objKey, val);
      }
      onComplete?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Arrive Patient</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <FormControl>
          <RadioGroup
            value={status}
            onChange={(e) => setStatus(e.target.value as 'W' | 'R' | '')}
          >
            <FormControlLabel value="W" control={<Radio />} label="Waiting for Documentation" />
            <FormControlLabel value="R" control={<Radio />} label="Ready for Exam" />
            <FormControlLabel value="" control={<Radio />} label="Clear Status" />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={pending}>
          {pending ? 'Saving…' : 'Set Status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
